import { FunctionComponent } from "react"
import { useTranslation } from "react-i18next"

import { useAgentState } from "@/agent-state"
import CallReportForm from "@/components/ACWForm"
import CallPanel from "@/components/CallPanel"
import ProfileView from "@/components/ContactProfileView"
import * as connectPanelValue from "@/pages/CustomConnectPanel/value"

import { useACWButtonsHook } from "../CallPanel/ACWButtons/hook"
import { useCallPanelHook } from "../CallPanel/hook"
import { useCallViewHook } from "./hook"
import { ProcessingTimeWrapper } from "./processing-time-wrapper"

interface Props {
  currentCase: connectPanelValue.Decoder.CaseAndActivityFeeds | null
  studentProfile: connectPanelValue.Decoder.StudentProfile | null
}

const IdContainer: FunctionComponent<{
  studentId: string
  taskId: string
  voiceId: string
}> = ({ studentId, taskId, voiceId }) => {
  const { t } = useTranslation()

  const styles = {
    uuidContainer:
      "row-start-1 col-span-full h-fit px-4 pt-3 pb-1 font-label-small text-neutral-500 flex gap-x-2 items-center flex-wrap",
    uuid: "flex gap-x-1 items-center",
  }

  return (
    <div className={styles.uuidContainer} data-unmask>
      <div className={styles.uuid}>
        <span data-unmask>{t("ids.taskId")}</span>
        <span data-unmask>{taskId}</span>
      </div>
      <div className={styles.uuid}>
        <span data-unmask>{t("ids.voiceId")}</span>
        <span data-unmask>{voiceId}</span>
      </div>
      <div className={styles.uuid}>
        <span data-unmask>{t("ids.uuid")}</span>
        <span data-unmask>{studentId}</span>
      </div>
    </div>
  )
}

const CallView: FunctionComponent<Props> = ({
  currentCase,
  studentProfile,
}) => {
  const {
    contactIdForProcessingTime,
    form,
    formattedTaskId,
    formattedVoiceId,
    studentId,
  } = useCallViewHook({
    currentCase,
    studentProfile,
  })

  const { state } = useAgentState()
  const callPanelState = useCallPanelHook({
    campaignName: currentCase?.case?.campaign_id || "",
    phoneNumber: studentProfile?.phone_number,
  })

  const acwButtonState = useACWButtonsHook({
    currentCase,
    isInboundCall: state.context.taskMeta?.callDirection === "inbound",
  })

  const historyItem = state.context.historyItem

  return (
    <div
      className="
        grid size-full grid-cols-[3fr_2fr] gap-x-6 gap-y-2 px-12 pb-28
        lg:grid-rows-[repeat(3,max-content)] lg:px-34.5
      "
      data-testid="call-view"
    >
      <IdContainer
        taskId={formattedTaskId}
        voiceId={formattedVoiceId}
        studentId={studentId}
      />
      <ProfileView
        currentCase={currentCase}
        studentProfile={studentProfile}
        historyItem={historyItem}
      />
      <CallPanel
        acwButtonState={acwButtonState}
        callPanelState={callPanelState}
        currentCase={currentCase}
      />
      <CallReportForm
        className="
          rounded-sm border border-neutral-300 bg-neutral-100 px-4 py-6
          shadow-card
        "
        form={form}
        historyItem={historyItem}
        currentCase={currentCase}
      />
      {contactIdForProcessingTime && (
        <ProcessingTimeWrapper contactId={contactIdForProcessingTime} />
      )}
    </div>
  )
}

export default CallView
